import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom'; 
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay, faStop, faEllipsisH, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const FlipBook = () => {
  const authState = useSelector((state) => state.authReducer);
  const location = useLocation(); 
  const navigate = useNavigate();
  const [userId] = useState(authState.user._id);
  const [userName] = useState(authState.user.name);
  const [bookId] = useState(location.state.bookId); 

  const [notes, setNotes] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [currentPage, setCurrentPage] = useState(0);
  const notesPerPage = 1;
  const [isSpeaking, setIsSpeaking] = useState(false);

  const [currentEmoji, setCurrentEmoji] = useState("😐");
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiInterval, setEmojiInterval] = useState(250);

  const [speechInstance] = useState(new SpeechSynthesisUtterance());

  // Fetch Notes
  const fetchNotes = useCallback(() => {
    const config = {
      url: '/myreadingnotes/forflipbook',
      method: 'post',
      headers: { Authorization: authState.token },
      data: { bookId, userId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response && response.status) {
          setNotes(response.notes);
        } else {
          console.error('Read Comments data not found in response');
        }
      })
      .catch((error) => {
        console.error('Error fetching Read Comments data:', error);
      });
  }, [authState.token, fetchData, bookId, userId]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchNotes();
  }, [authState.isLoggedIn, fetchNotes]);

  // Play/Stop Speech
  const toggleSpeech = (noteText) => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      clearInterval(emojiInterval); 
      setShowEmoji(false); 
    } else {
      // Show emoji overlay and start interval
      setShowEmoji(true);
      const interval = setInterval(() => {
        setCurrentEmoji((prev) => (prev === "😮" ? "😐" : "😮"));
      }, 50);
      setShowEmoji(true);
      if(isSpeaking) {
        setEmojiInterval (interval);
      }
      speechInstance.text = noteText;
      speechInstance.rate = 1;
      speechInstance.pitch = 1;
      speechInstance.volume = 1;
      speechInstance.lang = 'hi-IN';

      speechInstance.onend = () => {
        setIsSpeaking(false); 
      };

      window.speechSynthesis.speak(speechInstance);
      setIsSpeaking(true);
    }
  };

  // useEffect(() => {
  //   return () => {
  //     if (emojiInterval) clearInterval(emojiInterval);
  //   };
  // }, [emojiInterval]);


  // Stop Speech on Page Change
  const handlePageChange = (index) => {
    setCurrentPage(index);
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // Close the FlipBook
  const handleClose = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel(); 
      setIsSpeaking(false);
    }
    // navigate("/flipbook-home");
    navigate(-1);
  };

  // Auto Skip Page When Reading is finished
  const handleAutoSkipPage = () => {
    // Stop the current speech if any
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  
    // Delay for 1 second before moving to the next page
    setTimeout(() => {
      // Calculate the next page index
      const nextPageIndex = currentPage + 1;
  
      // Check if next page exists
      if (nextPageIndex < Math.ceil(notes.length / notesPerPage)) {
        // Update the current page
        setCurrentPage(nextPageIndex);
  
        // Get the notes for the next page
        const nextPageNotes = notes.slice(nextPageIndex * notesPerPage, (nextPageIndex + 1) * notesPerPage);
  
        // Start reading the notes of the next page if available
        if (nextPageNotes.length > 0) {
          toggleSpeech(nextPageNotes[0].notesText);
        }
      } else {
        // console.log('No more pages to skip.');
      }
    }, 1000); // 1 second delay
  };

  // Slider Settings
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: handlePageChange, // Stop speech when page changes
    appendDots: (dots) => (
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <ul style={{ margin: '0px' }}> {dots} </ul>
      </div>
    ),
    customPaging: (i) => (
      <div
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: '#ddd',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '5px',
        }}
      >
        {i + 1}
      </div>
    ),
  };

  // Render Notes
  const renderNotes = () => {
    const startIndex = currentPage * notesPerPage;
    const selectedNotes = notes.slice(startIndex, startIndex + notesPerPage);

    return selectedNotes.map((note) => (
      <div key={note._id} className="p-1 text-gray-600 rounded-md shadow-md text-sm" style={{ height: '90vh', overflow: 'auto' }}>
        <div className="w-full h-full">
          <div className="text-sm text-gray-500 flex justify-between">
            <div>
              <p>FlipNotes@Think5Whys</p>
              <p>{userName}</p>
            </div>
            <div className="flex flex-row justify-center items-center text-xs">
              <button
                className="text-xs max-w-10 max-h-8 bg-orange-600 text-white"
                onClick={() => toggleSpeech(note.notesText)}
              >
                {isSpeaking ? <FontAwesomeIcon icon={faStop} /> : <FontAwesomeIcon icon={faPlay} />}
              </button>
              <p className="text-xs text-red-600 flex justify-center items-center">Page# </p>
              <p className="text-xs text-red-600 flex justify-center items-center">{note.pageNumber}</p>
              <button className="text-xs max-w-10 max-h-8" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <button className="text-xs max-w-10 max-h-8" onClick={handleAutoSkipPage}>
                <FontAwesomeIcon icon={faEllipsisH} />
              </button>
            </div>
          </div>

          <div className="flex flex-col">
            <p className="text-lg text-blue-600 bg-yellow-300">Book: {note.bookTitle}</p>
            <p className="text-lg text-white bg-purple-400 mb-4 h-8">{note.notesTitle}</p>
            {showEmoji && isSpeaking && (
              <p onClick={() => setShowEmoji(false)} className="max-w-10 max-h-8 text-3xl text-white">{currentEmoji}</p>
            )}
            <p className="text-m text-blue-900">{note.notesText}</p>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="my-2 mx-auto max-w-[600px] py-4 bg-lime-200 text-blue-800">
      {loading ? (
        <Loader />
      ) : (
        <Slider {...sliderSettings}>
          {notes.length === 0 ? (
            <div className="flex flex-col justify-center items-center p-4 text-gray-600 text-center h-96">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-2 text-red-600" />
              <p className="text-lg">No records available.</p>
            </div>
          ) : (
            notes.map((note, index) => (
              <div key={note._id || index}>{renderNotes()}</div> 
            ))
          )}
        </Slider>
      )}
    </div>
  );
};

export default FlipBook;
